import axios, { AxiosError, AxiosResponse } from 'axios';
import { handle } from '../../util/helpers';
import * as log from '../../util/log';
import * as heap from '../../util/heap';
import { isDev, isTest } from '../../util/envHelpers';

interface HandleRemoveBackgroundParams {
  imageUrl: string;
  currentListingUuid: string | undefined;
  treetId: string;
  shopName: string;
}

// Not an exhaustive list!
export enum BackgroundRemovalErrorResponseCode {
  UNKNOWN_FOREGROUND = 'unknown_foreground',
  FAILED_TO_READ_IMAGE = 'failed_to_read_image',
  RESOLUTION_TOO_HIGH = 'resolution_too_high',
  INVALID_DIMENSIONS = 'invalid_dimensions',
  INVALID_FILE_TYPE = 'invalid_file_type',
  FILE_TOO_LARGE = 'file_too_large',
  GENERIC_ERROR = 'error',
}

export const getHelperTextFromError = (
  responseCode: string | BackgroundRemovalErrorResponseCode
): string => {
  let response;
  switch (responseCode) {
    case BackgroundRemovalErrorResponseCode.UNKNOWN_FOREGROUND:
      response =
        'Could not identify foreground in image. Please choose a background with higher contrast and try again.';
      break;
    case BackgroundRemovalErrorResponseCode.FAILED_TO_READ_IMAGE:
      response = 'There was an error reading the image. Please try again.';
      break;
    case BackgroundRemovalErrorResponseCode.RESOLUTION_TOO_HIGH:
      response = 'The resolution on the image is too high. Please try with a new image.';
      break;
    case BackgroundRemovalErrorResponseCode.INVALID_DIMENSIONS:
      response =
        'The dimensions of the given image could not be read. Please try again or try a different image.';
      break;
    case BackgroundRemovalErrorResponseCode.INVALID_FILE_TYPE:
      response = 'Invalid file type. Please try again with a different image.';
      break;
    case BackgroundRemovalErrorResponseCode.FILE_TOO_LARGE:
      response = 'File is too large. Please try again with a different image.';
      break;
    case BackgroundRemovalErrorResponseCode.GENERIC_ERROR:
    default:
      response = 'Failed to remove background for this image. Please contact support@treet.co.';
      break;
  }

  return response;
};

// Background Removal Error class that holds a code that can be parsed
// for user-facing messages.
class BackgroundRemovalError extends Error {
  code: string | null;

  constructor(code: BackgroundRemovalErrorResponseCode | string | null = null, ...params: any[]) {
    // Pass remaining arguments (including vendor specific ones) to parent constructor
    super(...params);

    // Maintains proper stack trace for where our error was thrown
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, BackgroundRemovalError);
    }

    this.name = 'BackgroundRemovalError';
    // Custom debugging information
    this.code = code;
  }
}

type RemoveBgErrorResponse = {
  errors: {
    title: string;
    code?: BackgroundRemovalErrorResponseCode | string;
  }[];
};

export const handleRemoveBackground = async (params: HandleRemoveBackgroundParams) => {
  const { imageUrl, currentListingUuid, treetId, shopName } = params;
  // converts image to jpeg in case incoming image is webp which is not supported
  // in remove.bg
  const imageUrlJpg = `${imageUrl}-/format/jpeg/`;

  const formData = new FormData();
  formData.append('size', isDev || isTest ? 'preview' : 'auto');
  formData.append('image_url', imageUrlJpg);
  const [removeBgResponse, removeBgError] = (await handle(
    axios.post('https://api.remove.bg/v1.0/removebg', formData, {
      headers: {
        'X-API-KEY': process.env.REACT_APP_REMOVE_BG_API_KEY,
        Accept: 'application/json',
      },
    })
  )) as [AxiosResponse | undefined, AxiosError<RemoveBgErrorResponse> | undefined];

  if (removeBgError) {
    removeBgError.message = 'remove.bg call failed for image url';
    const responseCode: BackgroundRemovalErrorResponseCode | string =
      removeBgError?.response?.data?.errors?.[0]?.code || 'error';
    const error = new BackgroundRemovalError(responseCode, removeBgError);
    log.error(error, 'remove-bg-failed', {
      imageUrl,
      status: removeBgError.response?.status,
      responseCode,
      removeBgResponse: JSON.stringify(removeBgError.response),
      removeBgError: JSON.stringify(removeBgError),
    });
    throw error;
  }
  heap.trackRemovedBackground(currentListingUuid, treetId, shopName);
  return removeBgResponse?.data?.data.result_b64;
};
